import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { seoFirecrawlUsage, seoFirecrawlAction } from "@/lib/seo/seo-os.functions";
import { SeoPage, Panel, Stat, Pill, Loading, Empty, DataTable, num, stateLabel, stateTone } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/seo/firecrawl")({
  head: () => ({ meta: [{ title: "Deep page reader · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: FirecrawlPage,
});

function FirecrawlPage() {
  const [url, setUrl] = useState("");
  const load = useServerFn(seoFirecrawlUsage);
  const act = useServerFn(seoFirecrawlAction);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["seo-firecrawl"], queryFn: () => load() });
  const d = q.data as any;
  const rows = (d?.rows ?? []) as any[];

  const run = useMutation({
    mutationFn: (action: "scrape" | "map") => act({ data: { action, url } }),
    onSuccess: (r: any) => {
      toast.success(r.links ? `Found ${r.links.length} pages.` : `Read ${num(r.chars)} characters of page content.`);
      qc.invalidateQueries({ queryKey: ["seo-firecrawl"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "That page could not be read."),
  });

  return (
    <SeoPage
      title="Deep page reader"
      description="Reads pages our own lightweight reader cannot — competitor pages that only render with JavaScript — and lists every page on a competitor's site. Optional: nothing else depends on it."
    >
      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="grid gap-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Status" value={stateLabel(d?.state ?? "unconfigured")} />
            <Stat label="Credits used (recent)" value={num(d?.credits ?? 0)} />
            <Stat label="Recent failures" value={num(d?.failures ?? 0)} />
          </div>

          <Panel title="Read a page" subtitle="Use a full https address, for example a competitor's product page.">
            <div className="flex flex-wrap gap-2">
              <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/dscr-loans" className="max-w-md" />
              <Button onClick={() => run.mutate("scrape")} disabled={!url || run.isPending}>
                Read page
              </Button>
              <Button variant="outline" onClick={() => run.mutate("map")} disabled={!url || run.isPending}>
                List their pages
              </Button>
            </div>
          </Panel>

          <Panel title="Recent activity" subtitle="Every request, with credits and errors.">
            {rows.length === 0 ? (
              <Empty>No requests yet.</Empty>
            ) : (
              <DataTable
                head={["When", "What", "Target", "Result", "Credits"]}
                rows={rows.map((r) => [
                  new Date(r.created_at).toLocaleString(),
                  r.operation,
                  <span key={r.id} className="break-all text-xs">{r.target}</span>,
                  <Pill key={`${r.id}-s`} tone={r.status === "ok" ? "good" : "bad"}>{r.status === "ok" ? "ok" : (r.error ?? r.status)}</Pill>,
                  r.credits ?? "—",
                ])}
              />
            )}
            {d?.state !== "ok" ? <p className="mt-3 text-sm text-muted-foreground">{stateLabel(d?.state ?? "unconfigured")} — connect the reader to enable this.</p> : null}
            <span className="sr-only">{stateTone(d?.state ?? "unconfigured")}</span>
          </Panel>
        </div>
      )}
    </SeoPage>
  );
}
